import { useEffect, useRef, useState } from 'react';
import { MONTHS_FULL } from './vencimientosConfig';

const SEARCH_DEBOUNCE_MS = 550;

/* Filtros in-page:
   - Tipo: Todos / Seguros / Placas (GPS y Tenencia ocultos: no hay datos/modulo)
   - Estado, Mes, Aseguradora (solo en Todos/Seguros), busqueda. */

const TIPO_OPCIONES = [
  { value: 'todos', label: 'Todos' },
  { value: 'seguro', label: 'Seguros' },
  { value: 'placa', label: 'Placas' },
  { value: 'tenencia', label: 'Tenencia' },
  { value: 'renta', label: 'Renta' },
];

const ESTADO_OPCIONES = [
  { value: 'todos', label: 'Todos' },
  { value: 'por_vencer', label: 'Por vencer' },
  { value: 'vencido', label: 'Vencido' },
  { value: 'pagado', label: 'Pagado' },
  { value: 'programado', label: 'Programado' },
  { value: 'renovado', label: 'Renovado' },
  { value: 'condonado', label: 'Condonado' },
  { value: 'cancelado', label: 'Cancelado' },
];

export default function FiltersBar({
  tipo,
  estado,
  mes,
  search,
  aseguradora,
  aseguradoras = [],
  hasActiveFilters,
  onChange,
  onSearch,
  onReset,
}) {
  // Buscador con debounce: el input responde al instante (estado local), pero
  // la busqueda real (onSearch -> refetch) se dispara 350ms despues de la ultima
  // tecla, no en cada pulsacion.
  const [searchText, setSearchText] = useState(search || '');
  const debounceRef = useRef(null);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  // Sincroniza el input cuando el valor externo cambia (p.ej. "Limpiar filtros").
  useEffect(() => {
    setSearchText(search || '');
  }, [search]);

  // Limpia el timer pendiente al desmontar.
  useEffect(() => () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
  }, []);

  function handleSearchInput(value) {
    setSearchText(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => onSearchRef.current(value), SEARCH_DEBOUNCE_MS);
  }

  const estadoLabel = ESTADO_OPCIONES.find((o) => o.value === (estado || 'todos'))?.label;
  const mesLabel = mes === '' || mes === null || mes === undefined
    ? 'Todo el ano'
    : MONTHS_FULL[Number(mes) - 1];
  const aseguradoraLabel = !aseguradora || aseguradora === 'todos' ? 'Todas' : aseguradora;
  const showAseguradora = tipo === 'todos' || tipo === 'seguro';

  return (
    <div className="venc-filters">
      {/* Tipo como segmented control */}
      <div className="venc-seg" role="tablist" aria-label="Tipo">
        {TIPO_OPCIONES.map((o) => (
          <button
            key={o.value}
            type="button"
            className={tipo === o.value ? 'on' : ''}
            onClick={() => onChange('tipo', o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Estado */}
      <label className="venc-fl">
        <span className="l">Estado:</span>
        <span className="v">{estadoLabel}</span>
        <i className="pi pi-chevron-down" />
        <select value={estado || 'todos'} onChange={(e) => onChange('estado', e.target.value)}>
          {ESTADO_OPCIONES.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </label>

      {/* Aseguradora (solo seguros) */}
      {showAseguradora && (
        <label className="venc-fl">
          <span className="l">Aseguradora:</span>
          <span className="v">{aseguradoraLabel}</span>
          <i className="pi pi-chevron-down" />
          <select value={aseguradora || 'todos'} onChange={(e) => onChange('aseguradora', e.target.value)}>
            <option value="todos">Todas</option>
            {aseguradoras.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </label>
      )}

      {/* Mes */}
      <label className="venc-fl">
        <span className="l">Mes:</span>
        <span className="v">{mesLabel}</span>
        <i className="pi pi-chevron-down" />
        <select value={mes ?? ''} onChange={(e) => onChange('mes', e.target.value)}>
          <option value="">Todo el ano</option>
          {MONTHS_FULL.map((m, i) => (
            <option key={m} value={i + 1}>{m}</option>
          ))}
        </select>
      </label>

      <span className="grow" />

      {/* Busqueda */}
      <div className="venc-search">
        <i className="pi pi-search" />
        <input
          placeholder="Buscar cliente, poliza, placa o # budget..."
          value={searchText}
          onChange={(e) => handleSearchInput(e.target.value)}
        />
      </div>

      {hasActiveFilters && (
        <button type="button" className="venc-reset" onClick={onReset}>
          Limpiar filtros
        </button>
      )}
    </div>
  );
}
